import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Fade,
  FormControl,
  Grid,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import {
  adminCreateLessonHistoryAdminNote,
  adminDeleteLessonHistoryAdminNote,
  ILessonHistory,
  ILessonHistoryAdminNote,
} from '../../../../../../../../../store/V2/LessonHistorySlice';
import { TPTextField } from '../../../../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../../../../components/V2/Buttons';
import AdminNoteCard from '../../../../../../../../../components/V2/Cards/AdminNoteCard';
import { IUser } from '../../../../../../../../../store/UserSlice';

interface AdminNotesTabProps {
  lessonHistory: ILessonHistory;
}

export const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const AdminNotesTab: FunctionComponent<AdminNotesTabProps> = ({
  lessonHistory,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // State
  const [noteText, setNoteText] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const adminNotes = lessonHistory.adminNotes || [];

  const handleAddNote = async () => {
    setAddLoading(true);

    await dispatch(
      adminCreateLessonHistoryAdminNote({
        id: lessonHistory._id,
        description: noteText,
      })
    );

    setAddLoading(false);
  };

  const handleDeleteNote = (note: ILessonHistoryAdminNote) => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this note?',
    }).then(async () => {
      setDeleteLoading(true);

      await dispatch(
        adminDeleteLessonHistoryAdminNote({
          id: lessonHistory._id,
          noteId: note._id,
        })
      );

      setDeleteLoading(false);
    });
  };

  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Admin Notes
          </Typography>
        </div>
        <Grid
          container
          spacing={2}
          sx={{ maxHeight: '600px', overflow: 'auto' }}
        >
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TPTextField
                multiline
                rows={3}
                name="adminNoteText"
                size="small"
                label="Admin Note"
                placeholder="Enter here..."
                value={noteText}
                onChange={(event) => setNoteText(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <DarkLoadingButton
              disableElevation
              variant="contained"
              color="inherit"
              onClick={handleAddNote}
              loading={addLoading}
              disabled={!noteText.trim().length}
              sx={{ ...defaultButtonStyles }}
            >
              Add Note
            </DarkLoadingButton>
          </Grid>
          {adminNotes.map((item) => (
            <Grid item xs={12} key={item._id}>
              <AdminNoteCard
                disableDelete={deleteLoading}
                onDelete={() => handleDeleteNote(item)}
                note={{
                  _id: item._id,
                  createdAt: item.createdAt,
                  createdBy: item.createdBy as IUser,
                  description: item.description,
                }}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    </Fade>
  );
};

export default AdminNotesTab;
